import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home as HomeIcon, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { FormError, FormBanner } from '../components/FormError';
import useFormValidation from '../hooks/useFormValidation';
import SEO from '../components/SEO';
import GoogleButton from '../components/GoogleButton';

const ROLES = [
  { value: 'user', label: 'Propietario', desc: 'Voy a vender o arrendar mi vivienda' },
  { value: 'agent', label: 'Agente', desc: 'Soy agente inmobiliario independiente' },
  { value: 'agency', label: 'Inmobiliaria', desc: 'Represento una empresa inmobiliaria' },
  { value: 'developer', label: 'Constructora / Desarrollador', desc: 'Represento una constructora o desarrolladora' },
];

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/mi-cuenta';
  const { register } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', role: 'user', company_name: '' });
  const [consent, setConsent] = useState({ terms: false, privacy: false, marketing: false });
  const [loading, setLoading] = useState(false);
  const v = useFormValidation();

  const set = (k, val) => { setForm((f) => ({ ...f, [k]: val })); v.clearField(k); };
  const setCons = (k) => (e) => setConsent(c => ({ ...c, [k]: e.target.checked }));

  const submit = async (e) => {
    e.preventDefault();
    v.reset();
    const ok = v.validate({
      name: !form.name || form.name.trim().length < 2 ? 'El nombre debe tener al menos 2 caracteres.' : null,
      email: !form.email ? 'Email es obligatorio.'
        : !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) ? 'Email no tiene un formato válido.'
        : null,
      password: !form.password ? 'La contraseña es obligatoria.'
        : form.password.length < 6 ? 'La contraseña debe tener al menos 6 caracteres.'
        : null,
      consent: (!consent.terms || !consent.privacy) ? 'Debes aceptar los Términos y la Política de Datos.' : null,
    });
    if (!ok) return;
    setLoading(true);
    try {
      await register({
        ...form,
        consent_terms: consent.terms,
        consent_privacy: consent.privacy,
        consent_marketing: consent.marketing,
      });
      navigate(from, { replace: true });
    } catch (err) {
      v.applyError(err, 'No pudimos crear tu cuenta. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-habita-cream grid lg:grid-cols-2">
      <SEO title="Crear cuenta" description="Únete a habita.co y publica propiedades, gestiona tu marca como asesor o representa a tu inmobiliaria." noindex />
      <div className="flex flex-col p-8 lg:p-14">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <div className="w-9 h-9 rounded-md bg-habita flex items-center justify-center"><HomeIcon className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
          <span className="font-serif font-bold text-2xl text-habita">habita<span className="text-amber-500">.</span><span className="text-gray-800">co</span></span>
        </Link>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <h1 className="font-serif text-4xl lg:text-5xl font-bold text-habita-dark">Crea tu cuenta</h1>
            <p className="text-gray-600 mt-3">Únete a habita.co y publica tu primera propiedad gratis.</p>

            <form onSubmit={submit} className="mt-7 space-y-4" noValidate>
              <FormBanner msg={v.banner} testid="register-error-banner" />

              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Soy</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {ROLES.map(r => (
                    <button key={r.value} type="button" onClick={() => set('role', r.value)} data-testid={`register-role-${r.value}`} className={`p-3 rounded-xl border-2 text-left transition-all ${form.role === r.value ? 'border-habita bg-habita-mint' : 'border-gray-200'}`}>
                      <p className="text-xs font-semibold text-habita-dark">{r.label}</p>
                      <p className="text-[10px] text-gray-600 mt-0.5 leading-tight">{r.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              {form.role === 'developer' && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Nombre de la constructora <span className="text-gray-400 normal-case">(opcional)</span></Label>
                  <Input
                    value={form.company_name}
                    onChange={e => set('company_name', e.target.value)}
                    className="mt-1.5 h-12"
                    placeholder="Ej: Constructora Andina SAS"
                    data-testid="register-company-input"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Nombre completo</Label>
                <Input
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  className={`mt-1.5 h-12 ${v.errorClass('name')}`}
                  data-testid="register-name-input"
                  {...v.field('name')}
                />
                <FormError msg={v.fieldErrors.name} testid="register-error-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => set('email', e.target.value)}
                  className={`mt-1.5 h-12 ${v.errorClass('email')}`}
                  data-testid="register-email-input"
                  {...v.field('email')}
                />
                <FormError msg={v.fieldErrors.email} testid="register-error-email" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Teléfono</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  className={`mt-1.5 h-12 ${v.errorClass('phone')}`}
                  placeholder="+57 300 000 0000"
                  data-testid="register-phone-input"
                  {...v.field('phone')}
                />
                <FormError msg={v.fieldErrors.phone} testid="register-error-phone" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Contraseña</Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  className={`mt-1.5 h-12 ${v.errorClass('password')}`}
                  placeholder="Mínimo 6 caracteres"
                  data-testid="register-password-input"
                  {...v.field('password')}
                />
                <FormError msg={v.fieldErrors.password} testid="register-error-password" />
              </div>
              <div className="space-y-2 pt-1" data-testid="register-consent-block">
                <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={consent.terms} onChange={setCons('terms')} className="mt-0.5 shrink-0" data-testid="register-consent-terms" />
                  <span>Acepto los <Link to="/terminos" target="_blank" className="text-habita hover:underline">Términos y Condiciones</Link> de Habita. *</span>
                </label>
                <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={consent.privacy} onChange={setCons('privacy')} className="mt-0.5 shrink-0" data-testid="register-consent-privacy" />
                  <span>Autorizo el tratamiento de mis datos conforme a la <Link to="/politica-datos" target="_blank" className="text-habita hover:underline">Política de Tratamiento de Datos</Link> (Ley 1581 de 2012). *</span>
                </label>
                <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={consent.marketing} onChange={setCons('marketing')} className="mt-0.5 shrink-0" data-testid="register-consent-marketing" />
                  <span>Quiero recibir novedades y ofertas por email (opcional).</span>
                </label>
                <FormError msg={v.fieldErrors.consent} testid="register-error-consent" />
              </div>
              <Button type="submit" disabled={loading || !consent.terms || !consent.privacy} className="btn-habita w-full h-12 rounded-full font-semibold disabled:opacity-60" data-testid="register-submit-btn">
                {loading ? 'Creando cuenta...' : 'Crear cuenta gratis'}
              </Button>
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="h-px bg-gray-200 flex-1" />
              <span className="text-xs uppercase tracking-widest text-gray-400">o</span>
              <div className="h-px bg-gray-200 flex-1" />
            </div>
            <GoogleButton label="Registrarme con Google" />

            <p className="text-sm text-gray-600 mt-6 text-center">
              ¿Ya tienes cuenta? <Link to="/login" state={{ from }} className="font-semibold text-habita hover:underline">Inicia sesión</Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex relative bg-habita-dark p-12 flex-col justify-center">
        <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative text-white">
          <span className="text-xs uppercase tracking-widest text-amber-300 font-semibold">Comienza gratis</span>
          <h2 className="font-serif text-4xl lg:text-5xl font-bold mt-3 leading-tight">Tu primer paso al hogar ideal.</h2>
          <ul className="mt-8 space-y-4">
            {['Publica gratis tu primera propiedad', 'Llega a más de 2M de visitantes/mes', 'Agenda inspecciones y gestiona mensajes', 'Estadísticas de tus publicaciones', 'Cancela cuando quieras'].map(f => (
              <li key={f} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-amber-300" /></div>
                <span className="text-white/90">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
